import GokuDataGrid from '@/components/GokuDataGrid'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import AddIcons from '@/icons/AddIcons'
import DeleteIcons from '@/icons/DeleteOutlineIcons'
import EditIcons from '@/icons/EditIcons'
import { CreateOtherFinancialModalProps } from '@/modals/OtherFinancialModals/CreateModal'
import { UpdateOtherFinancialModalProps } from '@/modals/OtherFinancialModals/UpdateModal'
import { useTranslation } from 'react-i18next'
import { Button } from 'vnetwork-ui'
import { IAction } from 'vnetwork-ui/dist/components/GokuDataGrid/Actions'
import {
    deleteOtherFinancialItem,
    listOtherFinancialItems,
} from '../../services'
import { OtherFinancialItem, OtherFinancialItemListParams } from '../../types'
import { columns } from '../columns'

interface OtherFinancialsListProps {
    filters?: OtherFinancialItemListParams
}

const OtherFinancialsList: React.FC<OtherFinancialsListProps> = ({
    filters,
}) => {
    const { t } = useTranslation()
    const { setConfirmation } = useConfirmation()
    const { setModal } = useModal()
    // Delete mutation
    const { mutateAsync: deleteItemMutation } = useMutationWithHandler(
        {
            mutationFn: deleteOtherFinancialItem,
        },
        {
            reactQueryKeys: [QUERY_KEYS.REPORTS_BI_OTHER_FINANCIALS],
        }
    )

    const handleDeleteClick = (item: OtherFinancialItem) => {
        setConfirmation({
            open: true,
            title: t('confirm_delete'),
            description: t('confirm_delete_other_financial_message'),
            onConfirm: () => handleDeleteItem(item.id),
        })
    }

    const handleDeleteItem = async (id: number) => {
        try {
            await deleteItemMutation(id)
        } catch (error) {
            console.error('Failed to delete financial item:', error)
        }
    }

    const handleOpenCreate = () => {
        setModal<CreateOtherFinancialModalProps>(
            'OtherFinancialModals/CreateModal/index'
        )
    }

    const handleOpenEdit = (item: OtherFinancialItem) => {
        setModal<UpdateOtherFinancialModalProps>(
            'OtherFinancialModals/UpdateModal/index',
            {
                financialItem: item,
            }
        )
    }

    // DataGrid actions
    const dataGridActions: IAction<OtherFinancialItem>[] = [
        {
            onClick: handleOpenEdit,
            title: t('edit'),
            color: 'primary',
            icon: <EditIcons />,
        },
        {
            onClick: handleDeleteClick,
            title: t('delete'),
            color: 'error',
            icon: <DeleteIcons />,
        },
    ]

    return (
        <div className="space-y-2">
            <GokuDataGrid
                apiCall={async (_params) => {
                    const data = await listOtherFinancialItems(filters)
                    return {
                        results: data,
                        total: data.length,
                    }
                }}
                columns={columns(t)}
                actions={dataGridActions}
                dataGridProps={{
                    getRowId: (row) => row.id,
                    hideFooter: true,
                    hideFooterPagination: true,
                }}
                options={{
                    pageSize: 50,
                    title: (
                        <div className="flex justify-end p-2">
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                className="min-w-[200px]"
                                onClick={handleOpenCreate}
                                startIcon={<AddIcons />}
                            >
                                {t('create')}
                            </Button>
                        </div>
                    ),
                    showSearch: false,
                }}
                reactQueryKey={
                    `${QUERY_KEYS.REPORTS_BI_OTHER_FINANCIALS}` +
                    JSON.stringify(filters)
                }
            />
        </div>
    )
}

export default OtherFinancialsList
